import React from 'react';

interface VesselOption {
  id: string;
  name: string;
}

interface VesselReportFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  vesselFilter: string;
  onVesselFilterChange: (value: string) => void;
  crewSizeFilter: string;
  onCrewSizeFilterChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  dateFrom: string;
  onDateFromChange: (value: string) => void;
  dateTo: string;
  onDateToChange: (value: string) => void;
  uniqueVessels: VesselOption[];
  filteredCount: number;
  totalCount: number;
  onClearFilters: () => void;
  onFilterToday: () => void;
}

const VesselReportFilters: React.FC<VesselReportFiltersProps> = ({
  searchTerm, onSearchChange,
  vesselFilter, onVesselFilterChange,
  crewSizeFilter, onCrewSizeFilterChange,
  statusFilter, onStatusFilterChange,
  dateFrom, onDateFromChange,
  dateTo, onDateToChange,
  uniqueVessels,
  filteredCount, totalCount,
  onClearFilters, onFilterToday,
}) => {
  const hasActiveFilters = searchTerm || crewSizeFilter || statusFilter || vesselFilter || dateFrom || dateTo;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        <button
          onClick={onClearFilters}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Clear All Filters
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Search Vessels</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by vessel name..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Specific Vessel</label>
          <select
            value={vesselFilter}
            onChange={(e) => onVesselFilterChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Vessels</option>
            {uniqueVessels.map(vessel => (
              <option key={vessel.id} value={vessel.id}>{vessel.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Crew Size</label>
          <select
            value={crewSizeFilter}
            onChange={(e) => onCrewSizeFilterChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Sizes</option>
            <option value="small">Small (2 or fewer crew)</option>
            <option value="medium">Medium (3-5 crew)</option>
            <option value="large">Large (more than 5 crew)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Operational Status</label>
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="fully-operational">Fully Operational</option>
            <option value="partial-crew">Partial Crew</option>
            <option value="vacation-heavy">Vacation Heavy</option>
            <option value="no-active-crew">No Active Crew</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Quick Filter</label>
          <button
            onClick={onFilterToday}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Today
          </button>
        </div>
      </div>

      {hasActiveFilters && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center text-sm text-blue-800">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
            </svg>
            <span>
              Showing {filteredCount} of {totalCount} vessels
              {searchTerm && ` | Search: "${searchTerm}"`}
              {vesselFilter && ` | Vessel: ${uniqueVessels.find(v => v.id === vesselFilter)?.name}`}
              {crewSizeFilter && ` | Size: ${crewSizeFilter}`}
              {statusFilter && ` | Status: ${statusFilter.replace('-', ' ')}`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default VesselReportFilters;
